import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Card, Col, Row, Accordion, Form, InputGroup } from "react-bootstrap";
import { Link } from "react-router-dom";

/**
 * คู่มือการใช้งานระบบ — แยกตามเมนู/ฝ่าย
 * แต่ละโมดูลประกอบด้วยรายการเมนู (items) พร้อมขั้นตอนการใช้งาน (steps)
 * ผู้ใช้จะเห็นเฉพาะเมนูตามสิทธิ์ของตนเอง แต่คู่มือนี้รวบรวมไว้ทั้งหมดเพื่ออ้างอิง
 */
const MANUAL = [
  // ───────────────────────── ทั่วไป (ทุกคนที่ล็อกอิน) ─────────────────────────
  {
    id: "general",
    title: "ทั่วไป (ใช้ได้ทุกคน)",
    icon: "ri-home-4-line",
    role: "ผู้ใช้ทุกตำแหน่ง",
    desc: "เมนูพื้นฐานที่ผู้ใช้ทุกคนที่ล็อกอินเข้าถึงได้",
    items: [
      {
        menu: "ดาวน์โหลดเอกสาร",
        path: "/general/documents",
        steps: [
          "เปิดเมนู “ดาวน์โหลดเอกสาร” จะเห็นรายการเอกสาร แบบฟอร์ม และระเบียบของโรงเรียน",
          "ใช้ช่องค้นหา/ตัวกรองหมวดหมู่เพื่อหาเอกสารที่ต้องการ",
          "คลิกปุ่ม “ดาวน์โหลด” เพื่อบันทึกไฟล์ลงเครื่อง",
        ],
        note: "เฉพาะฝ่ายทะเบียน/วิชาการ และผู้ดูแลระบบ จึงจะอัปโหลด/แก้ไข/ลบเอกสารได้",
      },
      {
        menu: "ประกาศ/ข่าวสาร",
        path: "/general/announcements",
        steps: [
          "เปิดเมนู “ประกาศ/ข่าวสาร” เพื่ออ่านประกาศภายในโรงเรียน",
          "ประกาศที่ปักหมุด (pin) จะแสดงอยู่บนสุดเสมอ",
          "คลิกที่หัวข้อประกาศเพื่ออ่านรายละเอียดและไฟล์แนบ",
        ],
      },
      {
        menu: "ปฏิทินการศึกษา",
        path: "/general/calendar",
        steps: [
          "เปิดเมนู “ปฏิทินการศึกษา” เพื่อดูกำหนดการและกิจกรรมตลอดปีการศึกษา",
          "สลับมุมมองเดือน/สัปดาห์ และคลิกที่กิจกรรมเพื่อดูรายละเอียด เช่น วันสอบ วันหยุด การประชุม",
        ],
      },
      {
        menu: "ค้นหาข้อมูลนักเรียน",
        path: "/students/search",
        steps: [
          "เปิดเมนู “ค้นหาข้อมูลนักเรียน”",
          "พิมพ์ชื่อ–สกุล หรือรหัสนักเรียน แล้วกดค้นหา",
          "คลิกที่รายชื่อเพื่อดูข้อมูลรายละเอียดของนักเรียน",
        ],
      },
    ],
  },

  // ───────────────────────── แจ้งปัญหา / แจ้งซ่อม ─────────────────────────
  {
    id: "issues",
    title: "แจ้งปัญหา / แจ้งซ่อม",
    icon: "ri-tools-fill",
    role: "ผู้ใช้ทุกตำแหน่ง (กล่องรับเรื่อง = หัวหน้าฝ่าย/แอดมิน)",
    desc: "ระบบรับแจ้งปัญหาภายในโรงเรียน เช่น แจ้งซ่อม อุปกรณ์ชำรุด แล้วส่งต่อให้ฝ่ายที่รับผิดชอบ",
    items: [
      {
        menu: "แจ้งปัญหา / แจ้งซ่อม",
        path: "/issues/submit",
        steps: [
          "เลือก “ประเภทปัญหา” — ระบบจะแสดงฝ่ายที่เรื่องจะถูกส่งไปให้อัตโนมัติ",
          "กรอก “หัวข้อ” และ “รายละเอียด” ให้ชัดเจน (จำเป็นต้องกรอก)",
          "ระบุ “สถานที่ / จุดสังเกต” เช่น อาคาร 3 ชั้น 2 ห้อง 305 (ถ้ามี)",
          "แนบรูปภาพประกอบได้ เพื่อช่วยให้ฝ่ายซ่อมเข้าใจปัญหา",
          "กดปุ่ม “ส่งรายงาน” ระบบจะให้รหัสรายงานไว้ติดตามสถานะ",
        ],
      },
      {
        menu: "รายการที่ฉันแจ้ง",
        path: "/issues/mine",
        steps: [
          "เปิดเมนู “รายการที่ฉันแจ้ง” เพื่อดูเรื่องที่ตนเองเคยแจ้งทั้งหมด",
          "คลิกที่รายการเพื่อดูสถานะล่าสุดและความคืบหน้าการดำเนินการ",
        ],
      },
      {
        menu: "กล่องรับเรื่อง (ฝ่าย)",
        path: "/issues/inbox",
        steps: [
          "หัวหน้าฝ่ายจะเห็นเรื่องที่ถูกส่งมายังฝ่ายของตน",
          "เปิดเรื่อง อัปเดตสถานะ (รับเรื่อง / กำลังดำเนินการ / เสร็จสิ้น) และบันทึกหมายเหตุ",
        ],
        note: "ผู้ที่ไม่ใช่หัวหน้าฝ่าย/แอดมิน จะถูกปฏิเสธการเข้าถึงข้อมูลในกล่องรับเรื่อง",
      },
    ],
  },

  // ───────────────────────── ครูผู้สอน (pos 1) ─────────────────────────
  {
    id: "teaching",
    title: "ครูผู้สอน",
    icon: "ri-presentation-line",
    role: "ครูผู้สอน",
    desc: "งานประจำของครูผู้สอน: ดูวิชาที่สอน เช็คชื่อ ดูสถิติการเข้าเรียน และบันทึกผลการเรียน",
    items: [
      {
        menu: "วิชา / ห้องที่สอน",
        path: "/teaching/subject-class",
        steps: [
          "เปิดเมนูเพื่อดูรายวิชาและห้องเรียนทั้งหมดที่ตนรับผิดชอบในปีการศึกษาปัจจุบัน",
          "คลิกที่ห้องเรียนเพื่อดูรายชื่อนักเรียนในห้องนั้น",
        ],
      },
      {
        menu: "ตารางสอนของฉัน",
        path: "/teaching/my-timetable",
        steps: [
          "เปิดเมนูเพื่อดูตารางสอนรายสัปดาห์ของตนเอง",
          "ตารางแสดงคาบ เวลา วิชา และห้องเรียนในแต่ละวัน",
        ],
      },
      {
        menu: "เช็คชื่อนักเรียน",
        path: "/teaching/attendance",
        steps: [
          "ระบบแสดงเฉพาะวิชา/คาบที่ต้องสอน “ในวันนี้” แยกตามภาคเรียน",
          "คลิกปุ่ม “เช็คชื่อ ม.x/y” ที่คาบที่ต้องการ",
          "เลือกวันที่ (ค่าเริ่มต้นเป็นวันนี้) ระบบจะตั้งสถานะนักเรียนทุกคนเป็น “มาเรียน” ให้อัตโนมัติ",
          "ปรับสถานะนักเรียนที่ ขาด/ลา/สาย เป็นรายคน",
          "กดบันทึก ระบบจะจดจำการเช็คชื่อ (พักไว้ชั่วคราวในเครื่องจนกว่าจะบันทึกถาวร)",
        ],
        note: "นักเรียนที่พ้นสภาพจะถูกตั้งเป็น “ขาด” โดยอัตโนมัติ คาบที่สอนติดกันจะถูกรวมเป็นรายการเดียว",
      },
      {
        menu: "ข้อมูลการเช็คชื่อ",
        path: "/teaching/manage-attendance",
        steps: [
          "เลือกวิชาที่ต้องการ เพื่อดูประวัติการเช็คชื่อย้อนหลังของวิชานั้น",
          "ตรวจสอบรายการที่เคยเช็ค และเข้าไปแก้ไขการเช็คชื่อของวัน/คาบที่ต้องการได้",
        ],
      },
      {
        menu: "สถิติการเข้าเรียน",
        path: "/teaching/attendance-statistic",
        steps: [
          "เลือกวิชาเพื่อดูสถิติการเข้าเรียนของนักเรียนแต่ละคนในวิชานั้น",
          "ใช้ตรวจสอบนักเรียนที่ขาดเรียนบ่อย เพื่อติดตามดูแล",
        ],
      },
      {
        menu: "บันทึกผลการเรียน",
        path: "/teaching/saveGrade",
        steps: [
          "เลือกปีการศึกษา (มุมขวาบน) ระบบจะแสดงรายวิชาที่ตนสอน",
          "คลิกที่วิชาเพื่อเข้าหน้ากรอกคะแนน/ผลการเรียนของนักเรียนรายคน",
          "กรอกคะแนนเก็บ คะแนนสอบ และผลการประเมิน แล้วบันทึก",
        ],
      },
    ],
  },

  // ───────────────────────── ครูประจำชั้น (pos 2) ─────────────────────────
  {
    id: "advisor",
    title: "ครูประจำชั้น (ที่ปรึกษา)",
    icon: "ri-user-star-line",
    role: "ครูประจำชั้น",
    desc: "ดูแลนักเรียนในห้องที่ปรึกษา: ผลการเรียน คุณลักษณะ สุขภาพ และเอกสาร ปพ.",
    items: [
      {
        menu: "Dashboard ห้องเรียน",
        path: "/advisor/dashboard",
        steps: ["ดูภาพรวมของห้องที่ปรึกษา เช่น จำนวนนักเรียน สถิติการเข้าเรียน และข้อมูลสรุปต่างๆ"],
      },
      {
        menu: "ข้อมูลนักเรียน",
        path: "/advisor/student-in-advice",
        steps: [
          "ดูรายชื่อนักเรียนทั้งหมดในห้องที่ปรึกษา",
          "คลิกที่นักเรียนเพื่อดู/แก้ไขข้อมูลรายบุคคล และเข้าถึงระเบียนสะสม (ปพ.6 / ปพ.8)",
        ],
      },
      {
        menu: "ผลการเรียนรายวิชา",
        path: "/advisor/grade",
        steps: ["เลือกวิชาเพื่อดูผลการเรียนของนักเรียนทั้งห้องในรายวิชานั้น"],
      },
      {
        menu: "ผลการเรียนรายบุคคล",
        path: "/advisor/student-grade",
        steps: [
          "เลือกนักเรียนจากรายชื่อ",
          "ดูผลการเรียนทุกวิชาของนักเรียนคนนั้น แยกตามปีการศึกษา/ภาคเรียน",
        ],
      },
      {
        menu: "ใบเกรดรวมในชั้นเรียน",
        path: "/advisor/class-grade",
        steps: ["ดูตารางเกรดรวมของนักเรียนทั้งห้อง ใช้สรุปผลและพิมพ์รายงานผลการเรียนของห้อง"],
      },
      {
        menu: "บันทึกคุณลักษณะ",
        path: "/advisor/desirable",
        steps: [
          "เปิดเมนูเพื่อประเมินคุณลักษณะอันพึงประสงค์ของนักเรียนในห้อง",
          "ให้ระดับคะแนน/ผลการประเมินรายคน แล้วบันทึก",
        ],
      },
      {
        menu: "บันทึกส่วนสูงและน้ำหนัก",
        path: "/advisor/student-body-mass",
        steps: ["กรอกส่วนสูงและน้ำหนักของนักเรียนแต่ละคนตามภาคเรียน ระบบใช้ข้อมูลนี้คำนวณภาวะโภชนาการ"],
      },
      {
        menu: "บันทึกสุขภาพนักเรียน",
        path: "/advisor/student-health",
        steps: ["บันทึกข้อมูลสุขภาพ โรคประจำตัว การแพ้ และข้อมูลด้านสุขภาพอื่นๆ ของนักเรียน"],
      },
      {
        menu: "แบบ ปพ.ต่างๆ",
        path: "/advisor/student-document",
        steps: [
          "เลือกนักเรียนและประเภทเอกสาร ปพ. ที่ต้องการ",
          "ระบบสร้างเอกสารจากข้อมูลในระบบให้ พร้อมพิมพ์/บันทึกเป็น PDF",
        ],
      },
      {
        menu: "ตารางสอนห้องเรียน",
        path: "/advisor/timetable",
        steps: ["ดูตารางเรียนรายสัปดาห์ของห้องที่ปรึกษา"],
      },
    ],
  },

  // ───────────────────────── ผู้ดูแลการสอน (pos 5) ─────────────────────────
  {
    id: "manage-teaching",
    title: "ผู้ดูแลการสอน",
    icon: "ri-calendar-schedule-line",
    role: "ผู้ดูแลการสอน / แอดมิน",
    desc: "จัดโครงสร้างการสอน: คาบเรียน การมอบหมายผู้สอน ตารางสอน และติดตามการสอน",
    items: [
      {
        menu: "จัดการคาบเรียน",
        path: "/teaching/manage-period",
        steps: ["กำหนดคาบเรียนและช่วงเวลาของแต่ละคาบ (เช่น คาบ 1 เวลา 08:30–09:20) ซึ่งใช้ร่วมกันทั้งโรงเรียน"],
      },
      {
        menu: "จัดการการสอน",
        path: "/teaching/manage-teaching",
        steps: [
          "เลือกห้องเรียน",
          "มอบหมายว่าวิชาใดสอนโดยครูคนใดในห้องนั้น (สร้างความสัมพันธ์ วิชา–ครู–ห้อง)",
        ],
      },
      {
        menu: "จัดการตารางสอน",
        path: "/teaching/timetable-editor",
        steps: [
          "เลือกห้องเรียนที่ต้องการจัดตาราง",
          "ลากวาง/กำหนดวิชาลงในช่องวัน–คาบ เพื่อสร้างตารางสอนของห้อง",
          "บันทึกตาราง ระบบจะตรวจสอบการชนกันของคาบให้",
        ],
      },
      {
        menu: "ติดตามการสอน",
        path: "/teaching/teaching-tracking",
        steps: [
          "เลือกห้อง/วิชา เพื่อดูว่าครูได้บันทึกการสอน (เนื้อหาที่สอนในแต่ละคาบ) ครบหรือไม่",
          "ใช้ตรวจสอบความสม่ำเสมอของการบันทึกการสอนรายวิชา",
        ],
      },
    ],
  },

  // ───────────────────────── ทะเบียนและหลักสูตร (pos 8, 9) ─────────────────────────
  {
    id: "registration",
    title: "ทะเบียนและหลักสูตร",
    icon: "ri-book-2-line",
    role: "ฝ่ายทะเบียน / แอดมิน",
    desc: "จัดการข้อมูลพื้นฐานทางการศึกษา: ห้องเรียน นักเรียน หลักสูตร และรายวิชา",
    items: [
      {
        menu: "จัดการห้องเรียน",
        path: "/registration/manage-classes",
        steps: [
          "ดูรายการห้องเรียนทั้งหมดแยกตามระดับชั้น/ปีการศึกษา",
          "เพิ่ม/แก้ไขห้องเรียน กำหนดครูที่ปรึกษา และดูรายชื่อนักเรียนในห้อง",
        ],
      },
      {
        menu: "จัดการนักเรียน",
        path: "/registration/manage-students/1",
        steps: [
          "เลือกห้องเรียนเพื่อดูรายชื่อนักเรียน",
          "เพิ่มนักเรียนใหม่ แก้ไขข้อมูล อัปโหลดรูปนักเรียน หรือย้าย/เลื่อนชั้น",
          "ปุ่มสถานะใช้กำหนดนักเรียนที่พ้นสภาพ/ย้ายออก",
        ],
      },
      {
        menu: "จัดการหลักสูตร",
        path: "/registration/manage-curriculum",
        steps: ["กำหนดโครงสร้างหลักสูตรของแต่ละระดับชั้น และรายวิชาที่ต้องเรียนในแต่ละหลักสูตร"],
      },
      {
        menu: "จัดการรายวิชา",
        path: "/registration/manage-subjects",
        steps: ["เพิ่ม/แก้ไขรายวิชา รหัสวิชา ชื่อวิชา หน่วยกิต และประเภทวิชา (พื้นฐาน/เพิ่มเติม)"],
      },
    ],
  },

  // ───────────────────────── วัดผลและประเมินผล (pos 10, 11) ─────────────────────────
  {
    id: "assessment",
    title: "วัดผลและประเมินผล",
    icon: "ri-bar-chart-box-line",
    role: "ฝ่ายวัดผล / แอดมิน",
    desc: "ภาพรวมและรายงานผลการเรียนของทั้งโรงเรียน",
    items: [
      {
        menu: "เกรดรวมในชั้นเรียน",
        path: "/assessment/all-class-grade",
        steps: [
          "เลือกระดับชั้น/ห้องเรียน เพื่อดูเกรดรวมของนักเรียนทั้งห้อง",
          "ใช้ตรวจสอบความครบถ้วนของการกรอกเกรดในแต่ละห้อง",
        ],
      },
      {
        menu: "ผลสัมฤทธิ์รายวิชา",
        path: "/assessment/grade-distribution",
        steps: [
          "เลือกวิชา/ปีการศึกษา เพื่อดูการกระจายของเกรด (จำนวนนักเรียนในแต่ละช่วงเกรด)",
          "ใช้วิเคราะห์ผลสัมฤทธิ์ทางการเรียนของแต่ละรายวิชา",
        ],
      },
      {
        menu: "ผลการเรียนรายนักเรียน",
        path: "/assessment/student-grades",
        steps: ["ค้นหานักเรียนเพื่อดูผลการเรียนทุกวิชาแยกตามภาคเรียน/ปีการศึกษา"],
      },
    ],
  },

  // ───────────────────────── หัวหน้าวิชาการสามัญ (pos 3, 4, 17) ─────────────────────────
  {
    id: "academic",
    title: "หัวหน้าวิชาการสามัญ",
    icon: "ri-file-chart-line",
    role: "หัวหน้าวิชาการ / แอดมิน",
    desc: "รายงานการเข้าเรียนระดับโรงเรียนและตรวจสอบผลการเรียน",
    items: [
      {
        menu: "รายงานการเข้าเรียนวันนี้",
        path: "/academic/general-attendance-report",
        steps: [
          "ดูสรุปการเข้าเรียนของทุกห้องในวันนี้แบบเรียลไทม์",
          "ข้อมูลจะอัปเดตอัตโนมัติเมื่อครูเช็คชื่อเสร็จ (ไม่ต้องรีเฟรชหน้า)",
        ],
      },
      {
        menu: "รายงานการเข้าเรียนย้อนหลัง",
        path: "/academic/attendance-history-report",
        steps: ["เลือกช่วงวันที่/ห้องเรียน เพื่อดูสถิติการเข้าเรียนย้อนหลัง และส่งออกเป็นรายงาน"],
      },
      {
        menu: "ตรวจสอบผลการเรียนรายบุคคล",
        path: "/academic/student-grade-review",
        steps: ["ค้นหานักเรียนเพื่อตรวจสอบผลการเรียนรายบุคคลทุกวิชา/ทุกภาคเรียน"],
      },
    ],
  },

  // ───────────────────────── ฝ่ายปกครอง (pos 16) ─────────────────────────
  {
    id: "discipline",
    title: "ฝ่ายปกครอง",
    icon: "ri-shield-user-line",
    role: "ฝ่ายปกครอง / แอดมิน",
    desc: "ติดตามความประพฤติและการขาดเรียนของนักเรียน",
    items: [
      {
        menu: "ติดตามนักเรียนขาดเรียน",
        path: "/discipline/absence-monitor",
        steps: ["ดูรายชื่อนักเรียนที่ขาดเรียนบ่อยหรือขาดเรียนต่อเนื่อง เพื่อติดตามและดำเนินการ"],
      },
      {
        menu: "คะแนนความประพฤติ",
        path: "/discipline/conduct",
        steps: [
          "ค้นหานักเรียนเพื่อดู/บันทึกคะแนนความประพฤติ",
          "บันทึกการหักคะแนนตามรายการความผิด หรือเพิ่มคะแนนจากความดี",
        ],
      },
      {
        menu: "รายการความผิด",
        path: "/discipline/conduct-rules",
        steps: ["กำหนด/แก้ไขรายการความผิดและคะแนนที่หักสำหรับแต่ละความผิด"],
      },
      {
        menu: "อนุญาตกลับบ้าน",
        path: "/discipline/home-leave",
        steps: ["บันทึก/อนุมัติการขออนุญาตกลับบ้านของนักเรียน (โดยเฉพาะนักเรียนประจำ)"],
      },
    ],
  },

  // ───────────────────────── ห้องสมุด (pos 13) ─────────────────────────
  {
    id: "library",
    title: "ห้องสมุด",
    icon: "ri-book-3-line",
    role: "บรรณารักษ์ / แอดมิน",
    desc: "งานห้องสมุด: ยืม–คืน จองหนังสือ ค่าปรับ และจัดการข้อมูลหนังสือ",
    items: [
      {
        menu: "Dashboard ห้องสมุด",
        path: "/library/home",
        steps: ["ดูภาพรวมงานห้องสมุด เช่น จำนวนการยืม หนังสือค้างคืน และค่าปรับคงค้าง"],
      },
      {
        menu: "ค้นหาหนังสือ",
        path: "/library/book-search",
        steps: ["ค้นหาหนังสือจากชื่อเรื่อง ผู้แต่ง หรือรหัส และตรวจสอบสถานะ (ว่าง/ถูกยืม/ถูกจอง)"],
      },
      {
        menu: "ยืม-คืนหนังสือ",
        path: "/library/circulation",
        steps: [
          "ยืม: สแกน/กรอกรหัสสมาชิก แล้วสแกนรหัสเล่มหนังสือ ระบบบันทึกการยืมและกำหนดวันครบกำหนด",
          "คืน: สแกนรหัสเล่มที่นำมาคืน ระบบจะปิดรายการยืมและคำนวณค่าปรับหากเกินกำหนด",
        ],
      },
      {
        menu: "ต่ออายุการยืม",
        path: "/library/renewals",
        steps: ["ค้นหารายการยืมที่ต้องการต่ออายุ แล้วกดต่ออายุเพื่อขยายวันครบกำหนด"],
      },
      {
        menu: "จองหนังสือ",
        path: "/library/reservations",
        steps: ["บันทึกการจองหนังสือที่ถูกยืมอยู่ เมื่อมีผู้คืน ระบบจะกันเล่มไว้ให้ผู้จอง"],
      },
      {
        menu: "ค่าปรับห้องสมุด",
        path: "/library/fines",
        steps: ["ดูรายการค่าปรับคงค้างของสมาชิก และบันทึกการชำระค่าปรับ"],
      },
      {
        menu: "นำเข้าหนังสือ",
        path: "/library/acquisition",
        steps: ["บันทึกหนังสือใหม่ที่จัดซื้อ/รับบริจาคเข้าระบบ พร้อมรายละเอียดและจำนวนเล่ม"],
      },
      {
        menu: "สร้าง/พิมพ์บาร์โค้ด",
        path: "/library/barcode-labels",
        steps: ["เลือกเล่มหนังสือ แล้วสร้างและพิมพ์ป้ายบาร์โค้ดรหัสเล่มเพื่อติดที่ตัวเล่ม"],
      },
      {
        menu: "จัดการข้อมูลหนังสือ/เล่ม",
        path: "/library/book-management",
        steps: ["เพิ่ม/แก้ไขข้อมูลหนังสือ (bibliographic) และข้อมูลเล่ม (copy) แต่ละเล่ม"],
      },
      {
        menu: "จัดการข้อมูลแม่บท",
        path: "/library/master-data",
        steps: ["จัดการข้อมูลพื้นฐานของห้องสมุด เช่น หมวดหมู่ ประเภทสมาชิก เงื่อนไขการยืมและค่าปรับ"],
      },
    ],
  },

  // ───────────────────────── หอพัก (pos 99, 17) ─────────────────────────
  {
    id: "dormitory",
    title: "หอพัก",
    icon: "ri-building-4-line",
    role: "ผู้ดูแลหอพัก / แอดมิน",
    desc: "จัดการหอพักนักเรียนประจำ: อาคาร การจัดเข้าหอ และการย้ายหอ",
    items: [
      {
        menu: "Dashboard หอพัก",
        path: "/dormitory/home",
        steps: ["ดูภาพรวมหอพัก เช่น จำนวนนักเรียนประจำ ที่ว่าง และการใช้งานของแต่ละอาคาร"],
      },
      {
        menu: "จัดการหมู่บ้าน",
        path: "/dormitory/villages",
        steps: ["กำหนดกลุ่ม/หมู่บ้านของหอพัก ใช้จัดกลุ่มอาคารหอพัก"],
      },
      {
        menu: "จัดการหอพัก",
        path: "/dormitory/buildings",
        steps: ["เพิ่ม/แก้ไขอาคารหอพัก ห้องพัก และความจุของแต่ละห้อง"],
      },
      {
        menu: "จัดนักเรียนเข้าหอ",
        path: "/dormitory/allocation",
        steps: ["เลือกนักเรียนและกำหนดห้องพักที่จะเข้าอยู่ ระบบจะตรวจสอบที่ว่างของห้อง"],
      },
      {
        menu: "ย้ายออก/ย้ายหอ",
        path: "/dormitory/transfer",
        steps: ["บันทึกการย้ายนักเรียนระหว่างห้อง/อาคาร หรือการย้ายออกจากหอพัก"],
      },
      {
        menu: "ค้นหาหอว่าง",
        path: "/dormitory/vacancy",
        steps: ["ค้นหาห้อง/เตียงที่ยังว่าง เพื่อจัดนักเรียนเข้าพัก"],
      },
      {
        menu: "ค้นหานักเรียนในหอพัก",
        path: "/dormitory/student-search",
        steps: ["ค้นหาว่านักเรียนคนใดพักอยู่ห้อง/อาคารใด"],
      },
    ],
  },

  // ───────────────────────── ธนาคาร (pos 18) ─────────────────────────
  {
    id: "bank",
    title: "ธนาคารโรงเรียน",
    icon: "ri-bank-line",
    role: "งานธนาคาร / แอดมิน",
    desc: "ระบบบัญชีออมทรัพย์ของนักเรียน",
    items: [
      {
        menu: "Dashboard ธนาคาร",
        path: "/bank/dashboard",
        steps: ["ดูภาพรวมยอดเงินฝากรวม จำนวนบัญชี และรายการเคลื่อนไหวล่าสุด"],
      },
      {
        menu: "บัญชีนักเรียน",
        path: "/bank/accounts",
        steps: [
          "ดูรายการบัญชีนักเรียนทั้งหมดและยอดคงเหลือ",
          "คลิกที่บัญชีเพื่อดูรายละเอียดและประวัติการทำรายการของนักเรียนคนนั้น",
        ],
      },
      {
        menu: "บันทึกรายการ",
        path: "/bank/transactions",
        steps: [
          "เลือกบัญชีนักเรียน",
          "เลือกประเภทรายการ ฝาก หรือ ถอน แล้วกรอกจำนวนเงิน",
          "บันทึกรายการ ระบบจะปรับยอดคงเหลือให้อัตโนมัติ",
        ],
      },
    ],
  },

  // ───────────────────────── พัสดุครุภัณฑ์ (pos 19) ─────────────────────────
  {
    id: "inventory",
    title: "พัสดุครุภัณฑ์",
    icon: "ri-archive-line",
    role: "คลังพัสดุ / แอดมิน",
    desc: "ทะเบียนครุภัณฑ์และงานจัดซื้อจัดจ้าง",
    items: [
      {
        menu: "Dashboard พัสดุ",
        path: "/inventory/dashboard",
        steps: ["ดูภาพรวมครุภัณฑ์ มูลค่ารวม และสถานะการจัดซื้อ"],
      },
      {
        menu: "ทะเบียนครุภัณฑ์",
        path: "/inventory/assets",
        steps: [
          "ดู/เพิ่มรายการครุภัณฑ์ พร้อมรหัสครุภัณฑ์ ที่ตั้ง และสถานะ",
          "บันทึกการตรวจสอบ การซ่อมบำรุง และการจำหน่ายออก",
        ],
      },
      {
        menu: "ขอจัดซื้อ/จัดจ้าง",
        path: "/inventory/procurement",
        steps: ["สร้างใบขอจัดซื้อ/จัดจ้าง เลือกผู้ขาย ระบุรายการและจำนวน แล้วติดตามสถานะการอนุมัติ"],
      },
      {
        menu: "ผู้ขาย/คู่ค้า",
        path: "/inventory/vendors",
        steps: ["จัดการข้อมูลผู้ขาย/ผู้รับจ้าง เช่น ชื่อ ที่อยู่ และข้อมูลติดต่อ"],
      },
      {
        menu: "ตั้งค่าพัสดุ",
        path: "/inventory/settings",
        steps: ["กำหนดข้อมูลพื้นฐานของระบบพัสดุ เช่น หมวดหมู่ครุภัณฑ์ และค่าตั้งต่างๆ"],
      },
    ],
  },

  // ───────────────────────── วัสดุและของใช้ (pos 19) ─────────────────────────
  {
    id: "supply",
    title: "วัสดุและของใช้",
    icon: "ri-store-3-line",
    role: "คลังวัสดุ / แอดมิน",
    desc: "จัดการวัสดุสิ้นเปลือง การรับเข้า เบิกจ่าย และยืม–คืน",
    items: [
      {
        menu: "Dashboard วัสดุ",
        path: "/supply/dashboard",
        steps: ["ดูภาพรวมคลังวัสดุ ยอดคงเหลือ และรายการที่ใกล้หมด"],
      },
      {
        menu: "รายการวัสดุ",
        path: "/supply/items",
        steps: ["ดู/เพิ่ม/แก้ไขรายการวัสดุ พร้อมหน่วยนับและจำนวนคงเหลือ"],
      },
      {
        menu: "รับเข้าคลัง",
        path: "/supply/receive",
        steps: ["บันทึกการรับวัสดุเข้าคลัง เลือกวัสดุและจำนวน ระบบจะเพิ่มยอดคงเหลือให้"],
      },
      {
        menu: "เบิกวัสดุ",
        path: "/supply/requisitions",
        steps: ["สร้างใบเบิกวัสดุ เลือกรายการและจำนวนที่เบิก ระบบจะตัดยอดคงเหลือ"],
      },
      {
        menu: "ยืม-คืน",
        path: "/supply/borrow",
        steps: ["บันทึกการยืมวัสดุ/อุปกรณ์ และบันทึกการคืนเมื่อนำกลับมา"],
      },
      {
        menu: "ตั้งค่าวัสดุ",
        path: "/supply/settings",
        steps: ["กำหนดหมวดหมู่วัสดุ หน่วยนับ และค่าตั้งต้นของระบบวัสดุ"],
      },
    ],
  },

  // ───────────────────────── ภาคศาสนา (pos 50–54) ─────────────────────────
  {
    id: "islamic",
    title: "ภาคศาสนา (Islamic Education)",
    icon: "ri-moon-line",
    role: "บุคลากรภาคศาสนา",
    desc: "โมดูลการศึกษาภาคศาสนาใช้หน้าจอแบบขวาไปซ้าย (RTL) มีงานครูผู้สอน ที่ปรึกษา วัดผล และทะเบียน คล้ายภาคสามัญ",
    items: [
      {
        menu: "วิชาที่รับผิดชอบ / เช็คชื่อ (ครูผู้สอน)",
        path: "/islamic/my-subjects",
        steps: [
          "ดูรายวิชาภาคศาสนาที่ตนสอน",
          "เข้าเมนู “เช็คชื่อนักเรียน” เลือกคาบเพื่อเช็คชื่อ (ขั้นตอนเหมือนภาคสามัญ)",
          "บันทึกคะแนนภาคศาสนาผ่านหน้ากรอกคะแนนของแต่ละวิชา",
        ],
      },
      {
        menu: "จัดการผู้สอน / ติดตามการสอน",
        path: "/islamic/manage-teaching",
        steps: ["มอบหมายครูผู้สอนภาคศาสนาลงแต่ละวิชา/ห้อง และติดตามการบันทึกการสอน"],
      },
      {
        menu: "ห้องเรียนของฉัน (ที่ปรึกษา)",
        path: "/islamic/advisor/home",
        steps: ["ดูภาพรวมห้องที่ปรึกษาภาคศาสนา รายชื่อนักเรียน คะแนนและการเข้าเรียน"],
      },
      {
        menu: "ตารางคะแนนภาคศาสนา (วัดผล)",
        path: "/islamic/grades",
        steps: ["ดูตารางคะแนนรวมของนักเรียนภาคศาสนาในแต่ละชั้นเรียน"],
      },
      {
        menu: "จัดการห้อง/นักเรียน/หลักสูตร (ทะเบียน)",
        path: "/islamic/manage-rooms",
        steps: ["จัดการห้องเรียน รายชื่อนักเรียน และหลักสูตรของภาคศาสนา"],
      },
    ],
  },

  // ───────────────────────── ผู้ดูแลระบบ (pos 99) ─────────────────────────
  {
    id: "admin",
    title: "ผู้ดูแลระบบ",
    icon: "ri-shield-keyhole-line",
    role: "ผู้ดูแลระบบ",
    desc: "จัดการบุคลากร สิทธิ์ผู้ใช้ และการตั้งค่าระบบ",
    items: [
      {
        menu: "จัดการบุคลากร",
        path: "/admin/staff",
        steps: ["เพิ่ม/แก้ไขข้อมูลบุคลากร (ครู/เจ้าหน้าที่) และจัดการบัญชีผู้ใช้งาน"],
      },
      {
        menu: "กำหนดสิทธิ์ผู้ใช้",
        path: "/admin/permissions",
        steps: [
          "เลือกบุคลากร แล้วกำหนดตำแหน่ง/สิทธิ์ที่ได้รับ",
          "สิทธิ์เหล่านี้กำหนดว่าผู้ใช้จะเห็นเมนูใดบ้างในแถบด้านซ้าย",
        ],
      },
      {
        menu: "จัดการประเภทสิทธิ์",
        path: "/admin/positions",
        steps: ["เพิ่ม/แก้ไขประเภทตำแหน่ง (position) และความหมายของแต่ละสิทธิ์"],
      },
      {
        menu: "ตั้งค่าระบบ",
        path: "/admin/settings",
        steps: ["กำหนดค่าตั้งต้นของระบบ เช่น ปีการศึกษาปัจจุบัน และข้อมูลพื้นฐานของโรงเรียน"],
      },
      {
        menu: "ระบบแจ้งปัญหา (ประเภทปัญหา / หัวหน้าฝ่าย)",
        path: "/issues/admin/categories",
        steps: [
          "กำหนดประเภทปัญหา และฝ่ายที่รับผิดชอบแต่ละประเภท",
          "กำหนดผู้เป็นหัวหน้าฝ่าย เพื่อให้เรื่องที่แจ้งถูกส่งไปยังผู้รับผิดชอบที่ถูกต้อง",
        ],
      },
    ],
  },
];

const FAQ = [
  { q: "ลืมรหัสผ่าน / เข้าระบบไม่ได้ ทำอย่างไร?", a: "ติดต่อผู้ดูแลระบบ (ฝ่ายไอที) เพื่อรีเซ็ตรหัสผ่านให้ใหม่ หรือใช้เมนูเปลี่ยนรหัสผ่านในหน้าโปรไฟล์หากยังเข้าระบบได้" },
  { q: "ไม่เห็นเมนูบางอย่าง?", a: "เมนูจะแสดงตามสิทธิ์ของแต่ละตำแหน่ง หากต้องการสิทธิ์เพิ่มเติม โปรดติดต่อผู้ดูแลระบบเพื่อกำหนดตำแหน่งให้" },
  { q: "ต้องการเพิ่มเอกสาร/ประกาศ/กิจกรรม?", a: "เฉพาะฝ่ายทะเบียน วิชาการ และผู้ดูแลระบบเท่านั้นที่สามารถเพิ่ม/แก้ไข/ลบได้ หากต้องการเผยแพร่เอกสาร โปรดส่งให้ฝ่ายที่เกี่ยวข้อง" },
  { q: "ข้อมูลที่บันทึกหายไปหรือไม่อัปเดต?", a: "ลองรีเฟรชหน้าจอ และตรวจสอบว่าเลือกปีการศึกษา/ภาคเรียนถูกต้อง หากยังพบปัญหาให้แจ้งผ่านเมนู “แจ้งปัญหา / แจ้งซ่อม”" },
  { q: "เช็คชื่อแล้วต้องการแก้ไขย้อนหลัง?", a: "ครูผู้สอนเข้าเมนู “ข้อมูลการเช็คชื่อ” เลือกวิชาและวัน/คาบที่ต้องการ แล้วแก้ไขการเช็คชื่อได้" },
];

export default function Help() {
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");
  const [query, setQuery] = useState("");
  useEffect(() => {}, [skin]);

  // กรองโมดูล/เมนูตามคำค้นหา
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return MANUAL;
    return MANUAL
      .map((m) => {
        const moduleMatch =
          m.title.toLowerCase().includes(q) ||
          m.role.toLowerCase().includes(q) ||
          m.desc.toLowerCase().includes(q);
        const items = m.items.filter(
          (it) =>
            it.menu.toLowerCase().includes(q) ||
            it.steps.some((s) => s.toLowerCase().includes(q))
        );
        if (moduleMatch) return m; // ตรงที่ระดับโมดูล แสดงทุกเมนู
        if (items.length) return { ...m, items };
        return null;
      })
      .filter(Boolean);
  }, [query]);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ทั่วไป</Link></li>
              <li className="breadcrumb-item active" aria-current="page">คู่มือ/ติดต่อ</li>
            </ol>
            <h4 className="main-title mb-0">คู่มือการใช้งานระบบ / ติดต่อ-ช่วยเหลือ</h4>
          </div>
        </div>

        <Row className="g-3">
          <Col xl="8">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <Card.Title as="h6" className="mb-0">คู่มือการใช้งานแยกตามเมนู</Card.Title>
                <div style={{ minWidth: 220 }}>
                  <InputGroup size="sm">
                    <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                    <Form.Control
                      placeholder="ค้นหาเมนู / ฝ่าย / ขั้นตอน"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </Card.Header>
              <Card.Body>
                <p className="fs-sm text-secondary">
                  ระบบจะแสดงเฉพาะเมนูตามสิทธิ์/ตำแหน่งของผู้ใช้ในแถบด้านซ้าย คู่มือนี้รวบรวมวิธีใช้งานของทุกเมนูไว้เพื่ออ้างอิง
                  คลิกที่แต่ละหัวข้อเพื่อดูขั้นตอนการใช้งาน
                </p>

                {filtered.length === 0 && (
                  <div className="text-center text-secondary py-4">
                    <i className="ri-search-eye-line fs-3 d-block mb-2"></i>
                    ไม่พบหัวข้อที่ตรงกับ “{query}”
                  </div>
                )}

                <Accordion alwaysOpen className="accordion-custom" defaultActiveKey={["general"]}>
                  {filtered.map((m) => (
                    <Accordion.Item eventKey={m.id} key={m.id}>
                      <Accordion.Header>
                        <span className="d-inline-flex align-items-center justify-content-center bg-primary text-white rounded me-2" style={{ width: 32, height: 32, flexShrink: 0 }}>
                          <i className={`${m.icon} fs-16`}></i>
                        </span>
                        <span className="fw-medium">{m.title}</span>
                        <Badge bg="secondary" className="ms-2 fw-normal">{m.role}</Badge>
                      </Accordion.Header>
                      <Accordion.Body>
                        <p className="fs-sm text-secondary">{m.desc}</p>
                        {m.items.map((it, idx) => (
                          <div key={idx} className={idx > 0 ? "mt-3 pt-3 border-top" : ""}>
                            <h6 className="mb-2 d-flex align-items-center gap-2">
                              <i className="ri-arrow-right-circle-line text-primary"></i>
                              {it.menu}
                            </h6>
                            <ol className="fs-sm text-secondary mb-1 ps-3">
                              {it.steps.map((s, si) => (
                                <li key={si} className="mb-1">{s}</li>
                              ))}
                            </ol>
                            {it.note && (
                              <div className="fs-xs text-warning-emphasis bg-warning-subtle rounded px-2 py-1 d-inline-flex align-items-start gap-1">
                                <i className="ri-information-line"></i>
                                <span>{it.note}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>

                <hr className="my-4" />

                <h6 className="mb-3">คำถามที่พบบ่อย (FAQ)</h6>
                <Accordion className="accordion-custom">
                  {FAQ.map((f, i) => (
                    <Accordion.Item eventKey={String(i)} key={i}>
                      <Accordion.Header>{f.q}</Accordion.Header>
                      <Accordion.Body className="fs-sm text-secondary">{f.a}</Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="card-one mb-3">
              <Card.Header><Card.Title as="h6">เริ่มต้นใช้งานอย่างไร</Card.Title></Card.Header>
              <Card.Body>
                <ol className="fs-sm text-secondary mb-0 ps-3">
                  <li className="mb-2">ล็อกอินด้วยบัญชีที่ผู้ดูแลระบบกำหนดให้</li>
                  <li className="mb-2">เมนูในแถบด้านซ้ายจะแสดงตามสิทธิ์/ตำแหน่งของคุณ</li>
                  <li className="mb-2">เลือกปีการศึกษาให้ถูกต้องก่อนทำงาน (ในหน้าที่มีให้เลือก)</li>
                  <li className="mb-0">หากติดปัญหาการใช้งาน ใช้เมนู “แจ้งปัญหา / แจ้งซ่อม” หรือติดต่อผู้ดูแลระบบ</li>
                </ol>
              </Card.Body>
            </Card>

            <Card className="card-one">
              <Card.Header><Card.Title as="h6">ติดต่อ-ช่วยเหลือ</Card.Title></Card.Header>
              <Card.Body>
                <ul className="list-unstyled mb-0">
                  <li className="d-flex align-items-center gap-2 mb-3">
                    <i className="ri-user-settings-line fs-20 text-primary"></i>
                    <div><div className="fw-medium">ผู้ดูแลระบบ (ฝ่ายไอที)</div><small className="text-secondary">ปัญหาการเข้าใช้งาน/สิทธิ์/รหัสผ่าน</small></div>
                  </li>
                  <li className="d-flex align-items-center gap-2 mb-3">
                    <i className="ri-phone-line fs-20 text-primary"></i>
                    <div><div className="fw-medium">โทรศัพท์โรงเรียน</div><small className="text-secondary">ติดต่อสำนักงานในเวลาทำการ</small></div>
                  </li>
                  <li className="d-flex align-items-center gap-2">
                    <i className="ri-mail-line fs-20 text-primary"></i>
                    <div><div className="fw-medium">อีเมล</div><small className="text-secondary">ส่งคำถาม/ข้อเสนอแนะเกี่ยวกับระบบ</small></div>
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
